import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, Tooltip, IconButton, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useQueryCoordinate, useMutationCoordinate } from '../../../clients/graphql/graphql.hooks';
import { gql } from '@apollo/client';
import { WorkflowSpansTab } from './WorkflowSpansTab';
import cronstrue from 'cronstrue';

const GET_WORKFLOW_CRONS = gql`
  query GetWorkflowCrons {
    listWorkflows {
      id
      name
      crons {
        name
        cron
        timeZone
      }
    }
  }
`;

const EXECUTE_CRON = gql`
  mutation ExecuteCron($input: ExecuteCronInput!) {
    executeCron(input: $input) {
      workflowId
      success
    }
  }
`;

interface WorkflowCronsTabProps {
  workflowId: string;
}

export const WorkflowCronsTab: React.FC<WorkflowCronsTabProps> = ({ workflowId }) => {
  const { loading, error, data } = useQueryCoordinate(GET_WORKFLOW_CRONS, {});
  const [executeCron, { loading: executingCron }] = useMutationCoordinate(EXECUTE_CRON, {});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedCron, setSelectedCron] = useState<{ name: string; workflowName: string } | null>(null);

  const getCronInEnglish = (cronExpression: string) => {
    try {
      return cronstrue.toString(cronExpression);
    } catch (err) {
      return "Invalid cron expression";
    }
  };

  const handleOpenConfirmDialog = (workflowName: string, cronName: string) => {
    setSelectedCron({ workflowName, name: cronName });
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleRunNow = () => {
    if (selectedCron) {
      executeCron({
        variables: {
          input: {
            workflowName: selectedCron.workflowName,
            cronName: selectedCron.name
          }
        }
      });
      setConfirmDialogOpen(false);
    }
  };

  if (loading) return <Typography>Loading crons...</Typography>;
  if (error) return <Typography color="error">Error loading crons: {error.message}</Typography>;

  const workflow = data?.listWorkflows?.find((wf: any) => wf.id === workflowId);
  const crons = workflow?.crons || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6">Cron Definitions</Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Cron Expression</TableCell>
              <TableCell>Time Zone</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {crons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No crons configured for this workflow
                </TableCell>
              </TableRow>
            ) : (
              crons.map((cron: any) => (
                <TableRow key={cron.name}>
                  <TableCell>{cron.name}</TableCell>
                  <TableCell>
                    {cron.cron}
                    <Tooltip title={getCronInEnglish(cron.cron)} arrow placement="top">
                      <IconButton size="small" sx={{ ml: 1 }}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{cron.timeZone}</TableCell>
                  <TableCell>
                    <Button 
                      variant="contained" 
                      size="small" 
                      disabled={executingCron}
                      onClick={() => handleOpenConfirmDialog(workflow.name, cron.name)}
                    >
                      Run Now
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6">Cron Executions</Typography>
      <WorkflowSpansTab workflowId={workflowId} spanType="CRON" />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
      >
        <DialogTitle>Confirm Cron Execution</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to manually run the cron job "{selectedCron?.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
          <Button 
            onClick={handleRunNow} 
            variant="contained" 
            color="primary" 
            disabled={executingCron}
          >
            {executingCron ? 'Executing...' : 'Execute Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 